import { generate } from "@ant-design/colors";
import { theme } from "antd";

// Utility function to convert HEX color to HSL color with optional lightness and alpha parameters
export const hexToHSL = (hex, lightness, alphaOrSaturation, alpha) => {
  try {
    hex = hex?.replace(/^#/, "");
    if (hex.length === 3) {
      hex = hex
        .split("")
        .map((hex) => hex + hex)
        .join("");
    }

    const r = parseInt(hex.substr(0, 2), 16) / 255;
    const g = parseInt(hex.substr(2, 2), 16) / 255;
    const b = parseInt(hex.substr(4, 2), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h,
      s,
      l = (max + min) / 2;

    if (max === min) {
      h = s = 0; // achromatic
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
        default:
          break;
      }
      h /= 6;
    }

    h = Math.round(h * 360);
    s = Math.round(s * 100);
    l = lightness !== undefined ? lightness : Math.round(l * 100);

    // Check if the third parameter is alpha (0 <= alpha <= 1) or saturation
    let a = 1;
    if (alphaOrSaturation <= 1) {
      a = alphaOrSaturation; // Treat as alpha
    } else {
      s = alphaOrSaturation; // Treat as saturation
      a = alpha !== undefined ? alpha : 1; // Alpha comes from the fourth argument if saturation is provided
    }

    return `hsl(${h}deg ${s}% ${l}% / ${a})`;
  } catch (error) {
    console.log({ error });
  }
};

// Configure ant component in different theme

// Define the purple theme configuration
const purpleTheme = (primaryColor) => {
  return {
    Menu: {
      itemBg: "red",
    },
    Modal: {
      colorBgElevated: "rgb(61 32 61)",
      contentBg: "rgb(61 32 61)",
      headerBg: "rgb(61 32 61)",
      titleColor: "rgba(255, 255, 255, 0.88)",
      colorText: "rgba(255, 255, 255, 0.88)",
      colorIcon: "rgb(255, 255, 255)",
      colorIconHover: "rgba(255, 255, 255, 0.88)",
    },
    Drawer: {
      colorBgElevated: "rgb(61 32 61)",
      colorIcon: "rgba(255, 255, 255, 0.45)",
      colorIconHover: "rgba(255, 255, 255, 0.88)",
      colorText: "rgba(255, 255, 255, 0.88)",
    },
    Typography: {
      colorLink: "rgb(255, 255, 255)",
      // algorithm: true,
      colorTextBase: "white",
    },
    Popover: {
      // colorBgElevated: "rgb(69 37 62)",
      colorText: "white",
    },
    Tooltip: {
      // colorBgSpotlight: "rgba(255, 255, 255, 0.08)",
      colorText: "white",
    },
    Card: {
      colorBgContainer: "rgba(38, 10, 47, 0.2)",
      boxShadowCard: "none",
      colorBorderSecondary: "rgba(255, 255, 255, 0.14)",
    },
    Button: {
      defaultBg: "rgba(255, 255, 255, 0.08)",
      defaultHoverBg: "rgba(255, 255, 255, 0)",
      primaryColor: "#10061A",
    },
    Input: {
      colorBgContainer: "rgba(255, 255, 255, 0.08)",
      colorTextPlaceholder: "rgb(255 255 255 / 35%)",
    },
    InputNumber: {
      colorBgContainer: "rgba(255, 255, 255, 0.08)",
      colorTextPlaceholder: "rgb(255 255 255 / 35%)",
    },
    Select: {
      colorBgContainer: "rgba(255, 255, 255, 0.08)",

      colorTextPlaceholder: "rgb(255 255 255 / 35%)",
      colorText: "white!important",
    },
    DatePicker: {
      colorBgContainer: "rgba(255, 255, 255, 0.08)",
      colorBgElevated: "#381937",
      colorTextPlaceholder: "rgb(255 255 255 / 35%)",
      backdropFilter: "blur(50px)",
    },

    Radio: {
      colorText: "white",
    },
    Segmented: {
      itemActiveBg: "rgba(220, 28, 28, 0.15)",
      itemSelectedBg: "rgba(255, 255, 255, 0.08)",
      trackBg: "rgba(245, 245, 245, 0.12)",
    },
    Table: {
      fontFamily: "'Inter', sans-serif",
      bodySortBg: "rgba(255, 255, 255, 0)",
      borderColor: "rgba(255, 255, 255, 0.14)",
      filterDropdownBg: "rgb(52, 22, 53)",
      filterDropdownMenuBg: "rgb(52, 22, 53)",
      headerBg: "rgb(51, 20, 51)",
      footerBg: "rgb(51, 20, 51)",
      headerSortActiveBg: "rgba(255, 255, 255, 0.01)",
      headerSortHoverBg: "rgba(52, 22, 53, 0)",
      headerSplitColor: "rgba(255, 254, 254, 0.14)",
      rowHoverBg: `#48253d`,
      rowSelectedBg: "rgb(51, 20, 51)",
      rowSelectedHoverBg: "rgb(51, 20, 51)",
      headerFilterHoverBg: "rgba(255, 255, 255, 0.08)",
      rowExpandedBg: "rgb(51, 20, 51)",
      colorBgContainer: "rgb(51, 20, 51)",
    },
    Breadcrumb: {
      itemColor: "rgba(255, 255, 255, 0.45)",
      lastItemColor: "rgba(255, 255, 255, 0.88)",
      colorText: "rgb(255, 255, 255)",
      linkColor: "rgb(255, 255, 255)",
      separatorColor: "rgba(255, 255, 255, 0.45)",
    },
    Badge: {
      colorBgContainer: "white",
    },
    Collapse: {
      contentBg: "rgba(242,0,0,0)",
    },
  };
};

// Define the dark theme configuration
const darkTheme = (primaryColor) => {
  return {
    Popover: {
      // colorBgElevated: "#141414",
      colorText: "White",
    },
    Tooltip: {
      // colorBgSpotlight: "#141414",
      colorText: "White",
    },
    Card: {
      colorBgContainer: "rgba(255, 255, 255, 0.08)",
      colorBorderSecondary: "rgba(255, 255, 255, 0.14)",
      colorText: "rgba(255, 255, 255, 0.85)",
      colorTextDescription: "rgba(255, 255, 255, 0.45)",
      colorTextHeading: "rgba(255, 255, 255, 0.85)",
      headerBg: "rgba(0, 0, 0, 0)",
      // extraColor: "rgba(0, 0, 0, 0.88)",
      actionsBg: "rgb(255, 255, 255)",
      // algorithm: true,
      boxShadowCard: "1px 1px 11px rgba(149, 149, 149, 0.16)",
    },
    Layout: {
      headerBg: "rgba(0, 24, 46, 0)",
      headerColor: "rgba(0, 0, 0, 0)",
      footerBg: "rgba(245, 245, 245, 0)",
      bodyBg: "rgb(0, 0, 0)",
      // algorithm: true,
    },
    Table: {
      fontFamily: "'Inter', sans-serif",
      headerSplitColor: "rgba(255, 255, 255, 0.14)",
      borderColor: "rgba(255, 255, 255, 0.14)",
      headerColor: "rgba(255, 255, 255, 0.88)",
      footerColor: "rgba(255, 255, 255, 0.88)",
      colorText: "rgba(255, 255, 255, 0.85)",
      colorTextDescription: "rgb(255, 255, 255)",
      // algorithm: true,
      rowHoverBg: `${hexToHSL(primaryColor, 4, 1)}`,
    },

    Breadcrumb: {
      itemColor: "rgba(255, 255, 255, 0.45)",
      lastItemColor: "rgba(255, 255, 255, 0.88)",
      colorText: "rgb(255, 255, 255)",
      linkColor: "rgb(255, 255, 255)",
      separatorColor: "rgba(255, 255, 255, 0.45)",
    },
    Select: {
      colorBgElevated: "rgb(31, 31, 31)",
      colorText: "white !important",
    },
    Input: {
      activeBg: "rgba(223, 31, 31, 0)",
      colorBgContainer: "rgba(255, 255, 255, 0.08)",
      colorText: "rgba(250, 250, 250, 0.88)",
      colorBorder: "rgba(255, 255, 255, 0.08)",
      // algorithm: true,
    },
    Typography: {
      colorLink: "rgb(255, 255, 255)",
      // algorithm: true,
      colorTextBase: "white",
    },
    // DatePicker: {
    //   algorithm: true,
    // },
    Radio: {
      colorText: "white",
    },
    Badge: {
      colorBgContainer: "white",
    },
    Button: {
      // defaultBg: "rgba(255, 255, 255, 0.08)",
      // defaultHoverBg: "rgba(255, 255, 255, 0)",
      primaryColor: "#10061A",
    },
  };
};

// Define the light theme configuration
const lightTheme = (primaryColor) => {
  return {
    Popover: {
      // colorBgElevated: "CardBackgroundAccentSolidColor",
      colorText: "black",
    },
    Tooltip: {
      colorBgSpotlight: "white",
      colorText: "black",
    },
    Card: {
      colorBgContainer: "white",
      colorBorderSecondary: "#f5f4f6",
      boxShadowCard: "1px 1px 11px rgba(149, 149, 149, 0.16)",
    },
    // Select: {
    //   algorithm: true,
    // },
    // Input: {
    //   algorithm: true,
    // },
    Layout: {
      headerBg: "rgba(0, 24, 46, 0)",
      headerColor: "rgba(0, 0, 0, 0)",
      footerBg: "rgba(245, 245, 245, 0)",
      bodyBg: "rgb(0, 0, 0)",
      // algorithm: true,
    },
    Table: {
      fontFamily: "'Inter', sans-serif",
      bodySortBg: "rgb(255, 255, 255)",
      expandIconBg: "rgb(255, 255, 255)",
      filterDropdownBg: "rgb(255, 255, 255)",
      headerColor: "rgba(0, 0, 0, 0.88)",
      fixedHeaderSortActiveBg: "rgb(255, 255, 255)",
      filterDropdownMenuBg: "rgb(255, 255, 255)",
      footerBg: "rgb(255, 255, 255)",
      headerBg: "rgb(255, 255, 255)",
      headerFilterHoverBg: "rgb(255, 255, 255)",
      headerSortActiveBg: "rgb(255, 255, 255)",
      headerSortHoverBg: "rgb(255, 255, 255)",
      rowSelectedHoverBg: "rgba(255, 255, 255, 0)",
      colorText: "rgb(38, 11, 59)",
      colorTextDescription: "rgb(38, 11, 59)",
      colorTextHeading: "rgb(38, 11, 59)",
      fontSize: 12,
      fontWeightStrong: 400,
      colorBgContainer: "white",
      // algorithm: true,
      rowHoverBg: `${hexToHSL(primaryColor, 97, 65, 1)}`,
    },
    Typography: {
      colorLink: "#260b3b",
      // algorithm: true,
    },
    // DatePicker: {
    //   algorithm: true,
    // },
    Radio: {
      colorText: "black",
    },
    Breadcrumb: {
      itemColor: "rgb(0, 0, 0)",
      lastItemColor: "rgba(0, 0, 0, 0.88)",
      colorText: "rgb(0, 0, 0)",
      linkColor: "rgb(0, 0, 0)",
      separatorColor: "rgb(0, 0, 0)",
    },
    Badge: {
      colorBgContainer: "white",
    },
  };
};

export const antSwitch = (
  themeId, // selected theme
  primaryColor, // Color
  languageId, //Language Id
  borderRadius
) => {
  let themeValue =
    themeId === "purple"
      ? purpleTheme(primaryColor)
      : themeId === "dark"
      ? darkTheme(primaryColor)
      : lightTheme(primaryColor);
  let fontSmlSize = {
    size: {
      width: 16,
      height: 16,
    },
    fontSize: "clamp(10px, 1vw, 16px)",
  };
  let fontMediumSize = {
    size: {
      width: 24,
      height: 24,
    },
    fontSize: "clamp(12px,1.5vw, 24px)",
  };
  let fontLgSize = {
    size: {
      width: 32,
      height: 32,
    },
    fontSize: "32px",
  };
  let colorSecondary = hexToHSL(primaryColor, 4, 1);
  let colorPrimaryShades = generate(primaryColor);
  let colorSecondaryShades = generate(colorSecondary);
  let borderColor = "#e0dcd9";
  let emptyStateSizes = { small: "100", medium: "150", large: "200" };
  let titleFontSize =
    languageId === "ar" ? "clamp(16px,1vw,22px)" : "clamp(10px,.9vw,16px)";
  let titleSecondaryFontSize =
    languageId === "ar" ? "clamp(16px,1.5vw,22px)" : "clamp(10px,1.2vw,20px)";
  let paraFontSize =
    languageId === "ar" ? "clamp(14px, 1vw, 20px)" : "clamp(8px, 0.8vw, 14px)";
  let btnFontSize =
    languageId === "ar" ? "clamp(14px, 1vw, 20px)" : "clamp(8px, 0.8vw, 14px)";
  let paraSecondaryFontSize =
    languageId === "ar"
      ? "clamp(12px, 0.9vw, 18px)"
      : "clamp(8px, 0.7vw, 12px)";
  let tableFontSize =
    languageId === "ar" ? "clamp(14px, 1vw, 20px)" : "clamp(8px, 0.78vw, 12px)";
  let tablePaddingSize = "clamp(4px, 0.4vw, 12px)";
  let CardBackgroundAccentColor = "white";
  let PopoverBackgroundAccentSolidColor = "white";
  let CardBackgroundAccentSolidColor = "white";
  let colorTextSecondary = "white";
  let colorTextAcsent = "white";
  let colorTextDisabled = hexToHSL(primaryColor, 4, 0.3);
  let newConfig = {
    CardBackgroundAccentColor: CardBackgroundAccentColor,
    borderColor: borderColor,
    cardBackground: "white",
    buttonHeight: "35px",
    CardBackgroundAccentSolidColor: CardBackgroundAccentSolidColor,
    backgroundColorSecondary: `${primaryColor}14`,
    colorSecondary: colorSecondary,
    colorPrimaryShades: colorPrimaryShades,
    colorSecondaryShades: colorSecondaryShades,
    colorText: colorSecondary,
    colorTextDisabled: colorTextDisabled,
    colorTextSecondary: colorTextSecondary,
    colorTextAcsent: colorTextAcsent,
    profileDropDownSecondaryBackground: hexToHSL(
      colorPrimaryShades[3],
      undefined,
      0.08
    ),
    backgroundHover: "rgb(0 0 0 / 4%)",
    backgroundAccentColor: hexToHSL(primaryColor, undefined, 0.08),
    headerDrawerItemBorder: " #e0dcd9",
    headerDrawerItemBorderHover: primaryColor,
    headerDrawerTextColor: colorSecondaryShades[4],
  };
  let inputHeight = "clamp(22px, 2.2vw, 40px)";
  // let inputHeight='clamp(38px, 3vw, 48px)';
  switch (themeId) {
    case "purple":
      newConfig = {
        ...newConfig,
        errorColor: "#FFA39E",
        textSecondary: "#C8C3CE",
        textSecondaryFontSize: "clamp(10px, 1vw, 14px)",
        collapseContentBgColor: "rgba(52,22,52,0.51)",
        tableRowHoverBg: `#48253d`,
        cardBorderColor: "rgb(255 255 255 / 8%)",
        themeColorSuccess: "#B7EB8F",
        themeColorError: "#FFA39E",
        dropdownbg: "rgb(66 32 65)",
        headerDrawerItemBorder: "#5E4E6E",
        headerDrawerItemBorderHover: primaryColor,
        headerDrawerTextColor: "white",
        algorithm: { algorithm: theme?.darkAlgorithm },
        colorTextSecondary: colorSecondary,
        colorTextAcsent: colorTextAcsent,
        borderColor: "white",
        backgroundColorSecondary: `${primaryColor}14`,
        colorText: colorSecondary,
        inputBorderColor: "#bd99c1",
        colorTextDisabled: "rgb(255 255 255 / 30%)",
        dashboardBackgroundColor:
          "radial-gradient(circle at top, #815b72 3%,#2e1420 60%,#260a2f 109%)",
        // cardBorder: "#ffffff23",
        tooltipBg: colorSecondary,
        backgroundAccentColor: "rgb(128 81 133 / 14%)",
        profileDropDownSecondaryBackground: hexToHSL(primaryColor, 0.8, 0.08),
        boxShadow: `1px 1px 20px 0px ${hexToHSL("#FFBB96", undefined, 0.08)}`,
        theme: "purple",
        backgroundHover: "rgb(128 81 133 / 14%)",
        colorPrimary: "#FFBB96",
        colorBgBase: "transparent",
        color: "white",
        bubbleBackground: "rgb(250 250 250 / 14%)",
        secondaryBackground: "rgb(250 250 250 / 4%)",
        colorLight: "rgb(0 0 0 / 14%)",
        color2: "white",
        breadcrumbsColor: "white",
        anchorColor: "white",
        colorHover: "#10061A",
        cardBoxShadow: "none",
        cardborder: "solid 1px rgba(250, 250, 250, 0.14)",
        cardBlur: "50px",
        cardBackground: "rgb(128 81 133 / 14%)",
        scrollBarBg: "rgb(103 68 103)",
        scrollBarBorder: "#2d0f2f",
        scrollTrackBg: "#2d0f2f",
        scrollTrackBorder: "#2d0f2f",
        drawerbackDropBlur: "2px",
        drawerbackGround: "rgb(66 32 65)",
        cursorSelectedbg: "white",
        cursorSelectedColor: "black",
        backdropColor: "rgb(0 0 0 / 14%)",
        backdropBlur: "2px",
        tooltipColor: "rgb(44 15 47)",
        tooltipBgColor: "white",
        dashboardBackground: "/pics/Dashboard/Dashboard-purple.png",
        titleFontSize: titleFontSize,
        titleSecondaryFontSize: titleSecondaryFontSize,
        emptyStateSizes: emptyStateSizes,
        paraFontSize: paraFontSize,
        btnFontSize: btnFontSize,
        tableFontSize: tableFontSize,
        tablePaddingSize: tablePaddingSize,
        paraSecondaryFontSize: paraSecondaryFontSize,
        fontLgSize: fontLgSize?.fontSize,
        fontMediumSize: fontMediumSize?.fontSize,
        fontSmlSize: fontSmlSize?.fontSize,
        inputHeight: inputHeight,
        CardBackgroundAccentSolidColor: "rgb(66 32 65)",
      };
      CardBackgroundAccentColor = "rgb(128 81 133 / 14%)";
      CardBackgroundAccentSolidColor = "rgb(66 32 65)";
      PopoverBackgroundAccentSolidColor = "rgb(66 32 65)";
      break;
    case "dark":
      newConfig = {
        ...newConfig,
        errorColor: "#FFA39E",
        textSecondary: "#C8C3CE",
        textSecondaryFontSize: "clamp(10px, 1vw, 14px)",
        collapseContentBgColor: "rgba(0,0,0,0)",
        tableRowHoverBg: `${hexToHSL(primaryColor, 4, 1)}`,
        cardBorderColor: "rgb(255 255 255 / 8%)",
        themeColorSuccess: "#B7EB8F",
        themeColorError: "#FFA39E",
        borderColor: "rgba(255, 255, 255, 0.14)",
        headerDrawerItemBorder: "rgba(255, 255, 255, 0.14)",
        headerDrawerItemBorderHover: primaryColor,
        headerDrawerTextColor: "white",
        inputBorderColor: "rgba(255, 255, 255, 0.08)",
        backgroundColorSecondary: `${primaryColor}14`,
        algorithm: { algorithm: theme?.darkAlgorithm },
        colorTextSecondary: colorSecondary,
        colorTextAcsent: colorTextAcsent,
        dashboardBackgroundColor: "black",
        colorText: colorSecondary,
        // cardBorder: "#ffffff23",
        tooltipBg: colorSecondary,
        colorTextDisabled: "rgb(255 255 255 / 30%)",
        backgroundAccentColor: hexToHSL(primaryColor, 2, 1),
        profileDropDownSecondaryBackground: hexToHSL(primaryColor, 0.8, 0.08),
        boxShadow: `1px 1px 20px 0px ${hexToHSL(primaryColor, 55, 0.1)}`,
        backgroundHover: "rgb(250 250 250 / 14%)",
        theme: "dark",
        colorPrimary: "#f16444",
        colorBgBase: "black",
        bubbleBackground: "rgb(250 250 250 / 14%)",
        secondaryBackground: "rgb(250 250 250 / 4%)",
        color: "white",
        color2: "white",
        breadcrumbsColor: "white",
        colorHover: "white",
        anchorColor: "white",
        cardBoxShadow: "1px 1px 11px rgba(149, 149, 149, 0.16)",
        cardborder: "solid 1px rgba(255, 255, 255, 0.14)",
        cardBlur: "0px",
        cardBackground: "#141414",
        scrollBarBg: "rgb(121 121 121)",
        scrollBarBorder: "rgb(75 75 75)",
        scrollTrackBg: "rgb(75 75 75)",
        scrollTrackBorder: "rgb(75 75 75)",
        drawerbackDropBlur: "5px",
        drawerbackGround: "#141414",
        cursorSelectedbg: "white",
        cursorSelectedColor: "black",
        backdropColor: "rgb(0 0 0 / 14%)",
        backdropBlur: "2px",
        dashboardBackground: "",
        tooltipColor: "#141414",
        tooltipBgColor: "white",
        titleFontSize: titleFontSize,
        titleSecondaryFontSize: titleSecondaryFontSize,
        paraFontSize: paraFontSize,
        btnFontSize: btnFontSize,
        tableFontSize: tableFontSize,
        tablePaddingSize: tablePaddingSize,
        paraSecondaryFontSize: paraSecondaryFontSize,
        emptyStateSizes: emptyStateSizes,
        fontLgSize: fontLgSize?.fontSize,
        fontMediumSize: fontMediumSize?.fontSize,
        fontSmlSize: fontSmlSize?.fontSize,
        dropdownbg: "#141414",
        Popover: {
          colorBgElevated: "#141414",
          colorText: "White",
        },
        Tooltip: {
          colorBgSpotlight: "#141414",
          colorText: "White",
        },

        inputHeight: inputHeight,
        CardBackgroundAccentSolidColor: hexToHSL(primaryColor, 2, 1),
      };
      CardBackgroundAccentColor = hexToHSL(primaryColor, 2, 1);
      CardBackgroundAccentSolidColor = hexToHSL(primaryColor, 2, 1);
      PopoverBackgroundAccentSolidColor = hexToHSL(primaryColor, 4, 1);
      break;
    default:
      newConfig = {
        ...newConfig,
        errorColor: "#F5222D",
        textSecondary: "#5d5c5f",
        textSecondaryFontSize: "clamp(10px, 1vw, 14px)",
        collapseContentBgColor: "rgba(255,255,255,0.1)",
        rowHoverBg: `${hexToHSL(primaryColor, 97, 65, 1)}`,
        cardBorderColor: hexToHSL(primaryColor, undefined, 0.14),
        themeColorSuccess: "#52C41A",
        themeColorError: "#FF7875",
        dropdownbg: "White",
        inputBorderColor: "#d9d9d9",
        headerDrawerItemBorder: "#e0dcd9",
        headerDrawerItemBorderHover: primaryColor,
        headerDrawerTextColor: colorSecondaryShades[4],
        backgroundColorSecondary: `${primaryColor}14`,
        colorTextSecondary: "white",
        colorTextAcsent: colorTextAcsent,
        colorTextDisabled: colorTextDisabled,
        borderColor: "#e0dcd9",
        algorithm: {},
        colorText: colorSecondary,
        dashboardBackgroundColor: `${colorSecondary}0d`,
        // cardBorder: `${colorSecondary}0d`,
        tooltipBg: colorSecondary,
        backgroundAccentColor: hexToHSL(primaryColor, undefined, 0.04),
        profileDropDownSecondaryBackground: hexToHSL(
          primaryColor,
          undefined,
          0.04
        ),
        boxShadow: `1px 1px 20px 1px ${hexToHSL(primaryColor, 50, 0.08)}`,
        backgroundHover: "rgb(0 0 0 / 4%)",
        theme: "default",
        colorPrimary: "#f16444",
        colorBgBase: "white",
        bubbleBackground: "rgb(0 0 0 / 8%)",
        secondaryBackground: "rgb(0 0 0 / 4%)",
        color: "black",
        color2: "white",
        breadcrumbsColor: "#757575",
        anchorColor: "#260b3b",
        colorHover: "white",
        cardBoxShadow: "1px 1px 11px rgba(149, 149, 149, 0.16)",
        cardborder: "solid 1px #e0dcd9",
        cardBlur: "0px",
        cardBackground: "white",
        scrollBarBg: "#f5f4f6",
        scrollBarBorder: "rgb(190 190 190)",
        scrollTrackBg: "rgb(230, 230, 230)",
        scrollTrackBorder: "rgb(230, 230, 230)",
        drawerbackDropBlur: "5px",
        drawerbackGround: "white",
        cursorSelectedbg: "#260b3b",
        cursorSelectedColor: "white",
        backdropColor: "rgb(0 0 0 / 14%)",
        backdropBlur: "2px",
        dashboardBackground: "",
        titleFontSize: titleFontSize,
        titleSecondaryFontSize: titleSecondaryFontSize,
        paraFontSize: paraFontSize,
        btnFontSize: btnFontSize,
        tableFontSize: tableFontSize,
        tablePaddingSize: tablePaddingSize,
        paraSecondaryFontSize: paraSecondaryFontSize,
        fontLgSize: fontLgSize?.fontSize,
        fontMediumSize: fontMediumSize?.fontSize,
        emptyStateSizes: emptyStateSizes,
        fontSmlSize: fontSmlSize?.fontSize,
        tooltipColor: "grey",
        tooltipBgColor: "white",

        inputHeight: inputHeight,
        CardBackgroundAccentSolidColor: "white",
      };
      CardBackgroundAccentColor = "white";
      CardBackgroundAccentSolidColor = "white";
      PopoverBackgroundAccentSolidColor = "white";
      break;
  }

  newConfig.components = {
    Table: {
      fontFamily: "'Inter', sans-serif",
      colorBgContainer: CardBackgroundAccentColor,
      bodySortBg: CardBackgroundAccentColor,
      expandIconBg: CardBackgroundAccentColor,
      filterDropdownBg: CardBackgroundAccentColor,
      fixedHeaderSortActiveBg: CardBackgroundAccentColor,
      filterDropdownMenuBg: CardBackgroundAccentColor,
      footerBg: CardBackgroundAccentColor,
      headerBg: CardBackgroundAccentColor,
      headerFilterHoverBg: CardBackgroundAccentColor,
      headerSortActiveBg: CardBackgroundAccentColor,
      headerSortHoverBg: CardBackgroundAccentColor,

      rowHoverBg: colorSecondaryShades[3],
      cellFontSize: tableFontSize,
      cellFontSizeMD: tableFontSize,
      cellFontSizeSM: tableFontSize,
      cellPaddingBlockSM: tablePaddingSize,
      ...themeValue?.Table,
    },
    Button: {
      fontFamily: "'Inter', sans-serif",
      controlHeight: "clamp(22px, 2vw, 32px)",
      controlHeightLG: "clamp(28px, 2.5vw, 40px)",
      defaultBg: CardBackgroundAccentColor,
      defaultBorderColor: colorSecondary,
      ...themeValue?.Button,
    },
    Modal: {
      fontFamily: "'Inter', sans-serif",
      contentBg: CardBackgroundAccentSolidColor,
      footerBg: CardBackgroundAccentSolidColor,
      headerBg: CardBackgroundAccentSolidColor,
      ...themeValue?.Modal,
    },
    Drawer: {
      fontFamily: "'Inter', sans-serif",
      colorBgElevated: CardBackgroundAccentSolidColor,
      ...themeValue?.Drawer,
    },
    Breadcrumb: {
      fontFamily: "'Inter', sans-serif",
      fontSize: paraSecondaryFontSize,
      ...themeValue?.Breadcrumb,
    },
    Card: {
      fontFamily: "'Inter', sans-serif",
      padding: 25,
      headerFontSize: titleFontSize,
      headerHeight: 50,
      fontWeightStrong: 400,
      boxShadowCard: "0px 8px 24px rgba(149, 149, 149, 0.16)",
      boxShadowTertiary: "0px 8px 24px rgba(149, 149, 149, 0.16)",
      ...themeValue?.Card,
    },
    Progress: {
      fontFamily: "'Inter', sans-serif",
      defaultColor: primaryColor,
    },
    Select: {
      controlHeight: "clamp(22px, 2vw, 32px)",
      controlHeightLG: "clamp(28px, 2.5vw, 40px)",
      fontFamily: "'Inter', sans-serif",
      optionPadding: "5px 20px",
      colorBgContainer: CardBackgroundAccentColor,
      // controlHeight: 32,
      // controlHeightLG: 48,
      // paddingBlock: 8,
      ...themeValue?.Select,
    },
    Input: {
      controlHeight: "clamp(22px, 2vw, 32px)",
      controlHeightLG: "clamp(28px, 2.5vw, 40px)",
      fontFamily: "'Inter', sans-serif",
      paddingBlock: 8,
      ...themeValue?.Input,
    },
    Typography: {
      fontFamily: "'Inter', sans-serif",
      fontSizeHeading1: 38,
      fontSizeHeading2: 30,
      fontSizeHeading3: 24,
      fontSizeHeading4: 20,
      fontSizeHeading5: 16,
      fontWeightStrong: 500,
      // algorithm: true,
      ...themeValue?.Typography,
    },
    DatePicker: {
      // controlHeight: "clamp(22px, 2vw, 32px)",
      // controlHeightLG: "clamp(28px, 2.5vw, 40px)",
      fontFamily: "'Inter', sans-serif",
      colorBgContainer: CardBackgroundAccentColor,
      colorBgElevated: CardBackgroundAccentColor,
      // algorithm: true,
      sizePopupArrow: 0,
      paddingBlock: 12,
      ...themeValue?.DatePicker,
    },
    TimePicker: {
      // controlHeight: "clamp(22px, 2vw, 32px)",
      // controlHeightLG: "clamp(28px, 2.5vw, 40px)",
      fontFamily: "'Inter', sans-serif",
      colorBgContainer: CardBackgroundAccentColor,
      colorBgElevated: CardBackgroundAccentColor,
      // algorithm: true,
      sizePopupArrow: 0,
      paddingBlock: 8,
      ...themeValue?.DatePicker,
    },
    InputNumber: {
      controlHeight: "clamp(22px, 2vw, 32px)",
      controlHeightLG: "clamp(28px, 2.5vw, 40px)",
      fontFamily: "'Inter', sans-serif",
      colorBgContainer: CardBackgroundAccentColor,
      ...themeValue?.InputNumber,
    },
    FloatButton: {
      fontFamily: "'Inter', sans-serif",
      controlItemBgHover: primaryColor,
    },
    Layout: {
      fontFamily: "'Inter', sans-serif",
      bodyBg: "none",
    },
    Timeline: {
      fontFamily: "'Inter', sans-serif",
      dotBg: "rgba(255, 254, 254, 0)",
    },
    Checkbox: {
      fontFamily: "'Inter', sans-serif",
      colorBgContainer: "rgba(255, 255, 255, 0)",
    },
    Radio: {
      borderRadius: `${borderRadius}px`,
      fontFamily: "'Inter', sans-serif",
      colorBgContainer: "rgba(255, 255, 255, 0)",
      buttonBg: "rgba(255, 255, 255, 0)!important",
      buttonCheckedBg: primaryColor + "!important",
      buttonColor: themeValue?.Radio?.colorText + "!important",
      buttonCheckedColor: colorSecondary + "!important",
      buttonSolidCheckedColor: colorSecondary + "!important",
      colorPrimary: primaryColor + "!important",
      ...themeValue?.Radio,
    },
    Segmented: {
      fontFamily: "'Inter', sans-serif",
      controlHeight: "clamp(22px, 2vw, 32px)",
      controlHeightLG: "clamp(28px, 2.5vw, 40px)",
      ...themeValue?.Segmented,
    },
    Popover: {
      fontFamily: "'Inter', sans-serif",
      sizePopupArrow: 0,
      fontWeightStrong: 500,
      colorBgElevated: PopoverBackgroundAccentSolidColor,
      ...themeValue?.Popover,
      sizePopupArrow: 0,
    },
    Tooltip: {
      fontFamily: "'Inter', sans-serif",
      paddingSM: 20,
      paddingXS: 20,
      sizePopupArrow: 0,
      colorBgSpotlight: CardBackgroundAccentSolidColor,
      colorBgElevated: CardBackgroundAccentSolidColor,
      ...themeValue?.Badge,
    },
    Badge: {
      fontFamily: "'Inter', sans-serif",
      ...themeValue?.Badge,
    },
    Steps: {
      colorTextLightSolid: colorSecondary,
    },
  };

  return newConfig;
};
// const themeDefault = useSelector((state) => state.themeDefault);
// themeDefault?.info?.themeColor
// const initialTheme = {
//   info: {
//     colorPrimary: "#FFBB96",
//     colorSecondary: "#260b3b",
//     // colorPrimaryShades: [
//     //   "#fff5f0",
//     //   "#fff1eb",
//     //   "#ffd3c2",
//     //   "#ffb399",
//     //   "#ff8f70",
//     //   "#f16444",
//     //   "#cc462f",
//     //   "#a62e1e",
//     //   "#801a11",
//     //   "#590f0b",
//     // ],
//     // colorSecondaryShades: [
//     //   "#78737a",
//     //   "#685b6e",
//     //   "#564161",
//     //   "#452b54",
//     //   "#351947",
//     //   "#260b3b",
//     //   "#0c0314",
//     //   "#000000",
//     //   "#000000",
//     //   "#000000",
//     // ],
//     // boxShadow: "1px 1px 20px 6px hsl(21deg 100% 79% / 0.16)",
//     // backgroundAccentColor: "hsl(21deg 100% 79% / 0.08)",
//     borderRadius: "40",
//     // themeColor: darkTheme,
//     themeColor: "lightTheme",
//     newConfig: {},
//     themeValue: lightTheme,
//     // themeColor: purpleTheme,
//     fontFamily: "'Inter', sans-serif",
//     fontFamilyTitle: "'Exo2', sans-serif",
//     // colorPrimaryBackground: "#f16444d1", // Default value based on initial colorPrimary
//   },
// };
